import useSWR from 'swr';
import axios, { endpoints, reValidate } from 'src/utils/axios';
import axiosInstance, { fetcher } from 'src/utils/axios';
import { useMemo } from 'react';
import {
  SparkTournamentDivisionDto,
  SparkTournamentDivisionTeamQueryDto,
  SparkTournamentDto,
  SparkTournamentTeamDto,
  SparkCreateTournamentDto,
  SparkCreateTournamentDivisionGroupDto,
  SparkUpdateTournamentDivisionGroupDto,
  SparkReorderGroupTeamsDto,
  SparkUpdateGroupTeamStatsDto,
  SparkTournamentDivisionMatchQueryDto,
  SparkTournamentDivisionGroupMatchDto,
  SparkTournamentDivisionKnockoutMatchDto,
  SparkCreateTournamentMatchDto,
  SparkUpdateTournamentDivisionMatchDto,
  SparkTournamentDivisionKnockoutStageDto,
  SparkTournamentDivisionGroupDto,
  SparkTournamentTeamCoreDto,
} from '../../../../libs/contracts/src';
import { TournamentDivisionTeamStatusEnum } from '../../../../libs/tournament/src/lib/enums';

// API hooks for tournaments
export function useGetTournaments() {
  const { data, error, isLoading, mutate } = useSWR(
    [endpoints.tournaments.list],
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );
  return useMemo(
    () => ({
      tournaments: data?.data.data as SparkTournamentDto[],
      tournamentsLoading: isLoading,
      tournamentsError: error,
      tournamentsEmpty: !data?.data?.data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, mutate],
  );
}

export function useGetTournament(id: string | number) {
  const { data, error, isLoading, mutate } = useSWR(
    id ? endpoints.tournaments.details(Number(id)) : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  return {
    tournament: data?.data as SparkTournamentDto,
    tournamentLoading: isLoading,
    tournamentError: error,
    refetch: mutate,
  };
}

// Tournament Divisions
export function useGetTournamentDivisions(tournamentId: string | number) {
  const { data, error, isLoading, mutate } = useSWR(
    tournamentId
      ? endpoints.tournaments.divisions.list(Number(tournamentId))
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  return useMemo(
    () => ({
      divisions: data?.data as SparkTournamentDivisionDto[],
      divisionsLoading: isLoading,
      divisionsError: error,
      refetch: mutate,
    }),
    [data, error, isLoading, mutate],
  );
}

export function useGetTournamentDivision(
  tournamentId: string | number,
  divisionId: string | number,
) {
  const { data, error, isLoading } = useSWR(
    tournamentId && divisionId
      ? endpoints.tournaments.divisions.details(
          Number(tournamentId),
          Number(divisionId),
        )
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  return useMemo(
    () => ({
      division: data?.data as SparkTournamentDivisionDto,
      divisionLoading: isLoading,
      divisionError: error,
      divisionValidating: false,
    }),
    [data, error, isLoading],
  );
}

// Tournament Teams
export function useGetTournamentTeams(
  tournamentId: string | number,
  divisionId: string | number,
  query?: SparkTournamentDivisionTeamQueryDto,
) {
  const { data, error, isLoading, mutate } = useSWR(
    tournamentId && divisionId
      ? [
          endpoints.tournaments.teams.list(
            Number(tournamentId),
            Number(divisionId),
          ),
          query || {},
        ]
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const result = useMemo(
    () => ({
      teams: (data?.data as SparkTournamentTeamDto[]) || [],
      teamsLoading: isLoading,
      teamsError: error,
      teamsEmpty: !data?.data?.length,
      teamsValidating: false,
      mutate,
    }),
    [data, error, isLoading, mutate],
  );

  return result;
}

// Tournament Teams
export function useGetTournamentTeamsCore(
  tournamentId: string | number,
  divisionId: string | number,
  groupId: string | number | null,
) {
  const { data, error, isLoading, mutate } = useSWR(
    tournamentId && divisionId
      ? [
          endpoints.tournaments.teams.core(
            Number(tournamentId),
            Number(divisionId),
          ),
          {
            params: {
              status: TournamentDivisionTeamStatusEnum.CONFIRMED,
              ...(groupId && { groupId }),
            },
          },
        ]
      : null,

    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const result = useMemo(
    () => ({
      teams: (data?.data as SparkTournamentTeamCoreDto[]) || [],
      teamsLoading: isLoading,
      teamsError: error,
      teamsEmpty: !data?.data?.length,
      teamsValidating: false,
      mutate,
    }),
    [data, error, isLoading, mutate],
  );

  return result;
}

export const approveTeam = async (
  tournamentId: string | number,
  divisionId: string | number,
  teamId: string | number,
) => {
  const response = await axios.put(
    `${endpoints.tournaments.teams.approve(Number(tournamentId), Number(divisionId), Number(teamId))}`,
  );
  reValidate(
    endpoints.tournaments.teams.list(Number(tournamentId), Number(divisionId)),
  );
  return response.data;
};

export const rejectTeam = async (
  tournamentId: string | number,
  divisionId: string | number,
  teamId: string | number,
) => {
  const response = await axios.put(
    `${endpoints.tournaments.teams.reject(Number(tournamentId), Number(divisionId), Number(teamId))}`,
  );
  reValidate(
    endpoints.tournaments.teams.list(Number(tournamentId), Number(divisionId)),
  );
  return response.data;
};

export const createTournament = async (data: SparkCreateTournamentDto) => {
  const response = await axios.post(endpoints.tournaments.add, data);
  reValidate(endpoints.tournaments.list);
  return response.data;
};

export const updateTournament = async (
  id: number,
  data: SparkCreateTournamentDto,
) => {
  const response = await axios.put(endpoints.tournaments.details(id), data);
  reValidate(endpoints.tournaments.list);
  reValidate(endpoints.tournaments.details(id));
  return response.data;
};

// Tournament Division Matches
export function useGetTournamentDivisionMatches(
  tournamentId: string | number,
  divisionId: string | number,
  filter: SparkTournamentDivisionMatchQueryDto,
) {
  const URL = [
    endpoints.tournaments.matches.list(
      Number(tournamentId),
      Number(divisionId),
    ),
    {
      params: {
        ...(filter.matchType && { matchType: filter.matchType }),
        ...(filter.groupId && { groupId: filter.groupId }),
        ...(filter.knockoutStageId && {
          knockoutStageId: filter.knockoutStageId,
        }),
        ...(filter.scheduledDate && {
          scheduledDate: new Date(filter.scheduledDate)
            .toISOString()
            .replace('T', ' ')
            .split('.')[0],
        }),
      },
    },
  ];

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    },
  );

  const memoizedValue = useMemo(() => {
    return {
      matches:
        (data?.data as (
          | SparkTournamentDivisionGroupMatchDto
          | SparkTournamentDivisionKnockoutMatchDto
        )[]) || [],
      matchesLoading: isLoading,
      matchesError: error,
      matchesValidating: isValidating,
      matchesEmpty: !isLoading && !data?.data?.length,
      mutate,
    };
  }, [data?.data, error, isLoading, isValidating, mutate]);

  return memoizedValue;
}

export const createTournamentDivisionMatch = async (
  tournamentId: string | number,
  divisionId: string | number,
  data: SparkCreateTournamentMatchDto,
) => {
  const response = await axios.post(
    endpoints.tournaments.matches.add(Number(tournamentId), Number(divisionId)),
    data,
  );
  reValidate(
    endpoints.tournaments.matches.list(
      Number(tournamentId),
      Number(divisionId),
    ),
  );
  return response.data;
};

export const updateTournamentDivisionMatch = async (
  tournamentId: string | number,
  divisionId: string | number,
  matchId: string | number,
  data: SparkUpdateTournamentDivisionMatchDto,
) => {
  const response = await axios.put(
    endpoints.tournaments.matches.update(
      Number(tournamentId),
      Number(divisionId),
      Number(matchId),
    ),
    data,
  );
  reValidate(
    endpoints.tournaments.matches.list(
      Number(tournamentId),
      Number(divisionId),
    ),
  );
  return response.data;
};

export const updateTournamentDivisionMatchResult = async (
  tournamentId: string | number,
  divisionId: string | number,
  matchId: string | number,
  data: {
    teamAScore: string;
    teamBScore: string;
    winnerTeamId: number | null;
    isDraw: boolean;
  },
) => {
  const response = await axios.put(
    endpoints.tournaments.matches.result(
      Number(tournamentId),
      Number(divisionId),
      Number(matchId),
    ),
    data,
  );
  reValidate(
    endpoints.tournaments.matches.list(
      Number(tournamentId),
      Number(divisionId),
    ),
  );
  return response.data;
};

// Tournament Division Groups
export function useGetTournamentDivisionGroups(
  tournamentId: string | number,
  divisionId: string | number,
) {
  const { data, error, isLoading, mutate } = useSWR(
    tournamentId && divisionId
      ? endpoints.tournaments.groups.list(
          Number(tournamentId),
          Number(divisionId),
        )
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );
  const result = useMemo(
    () => ({
      groups: (data?.data as SparkTournamentDivisionGroupDto[]) || [],
      groupsLoading: isLoading,
      groupsError: error,
      groupsValidating: false,
      groupsEmpty: isLoading && !data?.data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, mutate],
  );
  return result;
}

export async function createTournamentDivisionGroup(
  tournamentId: number,
  divisionId: number,
  data: SparkCreateTournamentDivisionGroupDto,
) {
  const URL = endpoints.tournaments.groups.add(tournamentId, divisionId);
  const response = await axiosInstance.post(URL, data);
  reValidate(endpoints.tournaments.groups.list(tournamentId, divisionId));
  return response.data;
}

export async function updateTournamentDivisionGroup(
  tournamentId: number,
  divisionId: number,
  groupId: number,
  data: SparkUpdateTournamentDivisionGroupDto,
) {
  const URL = endpoints.tournaments.groups.update(
    tournamentId,
    divisionId,
    groupId,
  );
  const response = await axiosInstance.put(URL, data);
  reValidate(endpoints.tournaments.groups.list(tournamentId, divisionId));
  return response.data;
}

export async function reorderGroupTeams(
  tournamentId: number,
  divisionId: number,
  groupId: number,
  data: SparkReorderGroupTeamsDto,
) {
  const URL = endpoints.tournaments.groups.reorder(
    tournamentId,
    divisionId,
    groupId,
  );
  const response = await axiosInstance.put(URL, data);
  reValidate(endpoints.tournaments.groups.list(tournamentId, divisionId));
  return response.data;
}

export async function updateGoupTeamStandings(
  tournamentId: number,
  divisionId: number,
  groupId: number,
  teamId: number,
  data: SparkUpdateGroupTeamStatsDto,
) {
  const URL = endpoints.tournaments.groups.updateTeamStandings(
    tournamentId,
    divisionId,
    groupId,
    teamId,
  );
  const response = await axiosInstance.put(URL, data);
  reValidate(endpoints.tournaments.groups.list(tournamentId, divisionId));
  return response.data;
}

// Tournament Division Knockout Stages
export function useGetTournamentDivisionKnockoutStages(
  tournamentId: string | number,
  divisionId: string | number,
) {
  const { data, error, isLoading, mutate } = useSWR(
    tournamentId && divisionId
      ? endpoints.tournaments.divisions.knockoutStages.list(
          Number(tournamentId),
          Number(divisionId),
        )
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  return useMemo(
    () => ({
      knockoutStages:
        (data?.data as SparkTournamentDivisionKnockoutStageDto[]) || [],
      knockoutStagesLoading: isLoading,
      knockoutStagesError: error,
      knockoutStagesEmpty: isLoading && !data?.data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, mutate],
  );
}
